import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, model, output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";
import { S25DatepickerModule } from "../../s25-datepicker/s25.datepicker.module";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { DEFAULT_EMAIL_SENT_FILTER, EmailSentFilter } from "./email.sent.util";

@TypeManagerDecorator("s25-ng-se-email-sent-filter")
@Component({
    selector: "s25-ng-se-email-sent-filter",
    host: { class: "email-sent-filter" },
    template: `
        <div class="filter-bar">
            <div class="filter-bar__label">
                <s25-ng-icon [type]="'cog'" [size]="16" [ariaLabel]="false" />
                <span>Filter:</span>
            </div>

            <fieldset class="filter-bar__controls">
                <legend class="visually-hidden">Filter options</legend>

                <div class="filter-group">
                    <span class="filter-group__label">Date range</span>
                    <select
                        class="filter-select"
                        name="datePreset"
                        aria-label="Filter by date range"
                        [ngModel]="filter().datePreset"
                        (ngModelChange)="onFilterChange('datePreset', $event)"
                    >
                        <option value="last7">Last 7 days</option>
                        <option value="last30">Last 30 days</option>
                        <option value="all">All time</option>
                        <option value="custom">Custom</option>
                    </select>
                    @if (showCustomDates()) {
                        <div class="date-range-pickers">
                            <s25-datepicker
                                [value]="filter().customStartDate"
                                [allowEmpty]="true"
                                [autoSelectToday]="false"
                                [placeholder]="'Start date'"
                                [ariaLabel]="'Filter start date'"
                                (valueChange)="onFilterChange('customStartDate', $event)"
                            />
                            <span class="date-range-separator" aria-hidden="true">—</span>
                            <s25-datepicker
                                [value]="filter().customEndDate"
                                [allowEmpty]="true"
                                [autoSelectToday]="false"
                                [placeholder]="'End date'"
                                [ariaLabel]="'Filter end date'"
                                (valueChange)="onFilterChange('customEndDate', $event)"
                            />
                        </div>
                    }
                </div>

                @if (showOrganizationsFilter()) {
                    <span class="filter-divider" aria-hidden="true"></span>

                    <div class="filter-group">
                        <span class="filter-group__label">Organizations</span>
                        <s25-ng-multiselect-search-criteria
                            class="se-filter-criteria-dropdown"
                            [type]="'organizations'"
                            [(value)]="organizationsModel"
                            [buttonText]="'Organizations'"
                            (onDone)="onOrganizationsDone($event)"
                        />
                    </div>
                }

                @if (showEventsFilter()) {
                    <span class="filter-divider" aria-hidden="true"></span>

                    <div class="filter-group">
                        <span class="filter-group__label">Events</span>
                        <s25-ng-multiselect-search-criteria
                            class="se-filter-criteria-dropdown"
                            [type]="'events'"
                            [(value)]="eventsModel"
                            [buttonText]="'Events'"
                            (onDone)="onEventsDone($event)"
                        />
                    </div>
                }

                <div class="filter-bar__search" role="search">
                    <label for="email-sent-search-input" class="visually-hidden">Search messages</label>
                    <s25-ng-icon [type]="'search'" [size]="16" class="search-icon" [ariaLabel]="false" />
                    <input
                        id="email-sent-search-input"
                        type="search"
                        name="emailSentSearchTerm"
                        class="search-input"
                        autocomplete="off"
                        aria-label="Search messages"
                        placeholder="Search sent mail by recipient, subject…"
                        [ngModel]="searchTermDraft()"
                        (ngModelChange)="onSearchTermDraftInput($event)"
                        (keydown.enter)="$event.preventDefault(); onSearchSubmit()"
                    />
                    @if (searchTermDraft()) {
                        <button class="clear-btn" type="button" aria-label="Clear search" (click)="clearSearchTerm()">
                            <s25-ng-icon [type]="'close'" [size]="14" [ariaLabel]="false" />
                        </button>
                    }
                </div>

                <button class="aw-button aw-button--primary search-btn" type="button" (click)="onSearchSubmit()">
                    Search
                </button>

                @if (hasActiveFilters()) {
                    <button class="clear-filters-btn" type="button" (click)="onClear()">
                        <s25-ng-icon [type]="'close'" [size]="14" [ariaLabel]="false" />
                        <span>Clear</span>
                    </button>
                }
            </fieldset>
        </div>
    `,
    styles: `
        .filter-bar {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1.25rem;
            padding: 1rem;
            border: 1px solid var(--color--border);
            border-radius: 6px;
            background: var(--color--surface-bg);
        }

        .filter-bar__label {
            display: flex;
            align-items: center;
            gap: 0.375rem;
            flex-shrink: 0;
            font-size: 0.875rem;
            font-weight: 600;
            color: var(--color--text);
        }

        .filter-bar__controls {
            display: flex;
            flex: 1;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
            border: none;
            padding: 0;
            margin: 0;
            min-width: 0;
        }

        .filter-group {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
            flex-shrink: 0;
        }

        .filter-group__label {
            font-size: 0.75rem;
            font-weight: 600;
            color: var(--color--text-muted);
            white-space: nowrap;
        }

        .filter-divider {
            width: 1px;
            height: 1.5rem;
            background: var(--color--border);
            flex-shrink: 0;
        }

        .filter-bar__search {
            flex: 1;
            min-width: 200px;
            position: relative;
        }

        .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        .search-icon {
            position: absolute;
            left: 0.625rem;
            top: 50%;
            transform: translateY(-50%);
            color: var(--color--text-muted);
            pointer-events: none;
        }

        .search-input,
        .filter-select {
            font-size: 0.8125rem;
            color: var(--color--text);
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: var(--border-radius);
            transition: box-shadow var(--transition-medium) ease;
        }

        .search-input {
            width: 100%;
            padding: 0.5rem 2rem 0.5rem 2.25rem;
        }

        .search-input:focus,
        .filter-select:focus {
            outline: none;
            background: var(--color--form-bg);
            box-shadow: var(--shadow--form-focus);
        }

        .clear-btn {
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: var(--color--text-muted);
            cursor: pointer;
            padding: 0.25rem;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius);
        }

        .filter-select {
            padding: 0.5rem 2rem 0.5rem 0.75rem;
            cursor: pointer;
            appearance: none;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right 0.5rem center;
            background-size: 16px;
            min-width: 120px;
        }

        .date-range-pickers {
            display: flex;
            gap: 0.375rem;
            align-items: center;
            flex-wrap: wrap;
        }

        .date-range-separator {
            color: var(--color--text-muted);
            font-size: 0.75rem;
        }

        :host ::ng-deep .filter-bar__controls s25-datepicker .c-input {
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: 1px solid var(--color--border) !important;
            border-radius: var(--border-radius);
            font-size: 0.8125rem;
            height: unset;
            background: var(--color--surface-2-bg);
        }

        :host ::ng-deep .filter-bar__controls .se-filter-criteria-dropdown .ngDropdownPaginated {
            padding: 2px;
            border: 1px solid var(--color--border) !important;
            border-radius: var(--border-radius);
            min-width: 140px;
            background: var(--color--surface-2-bg);
        }

        .search-btn {
            white-space: nowrap;
            flex-shrink: 0;
        }

        .clear-filters-btn {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.5rem 0.75rem;
            border: 1px solid transparent;
            border-radius: var(--border-radius);
            background: none;
            color: var(--color--text-muted);
            font-size: 0.8125rem;
            cursor: pointer;
            flex-shrink: 0;
            transition:
                color var(--transition-medium) ease,
                background var(--transition-medium) ease,
                border-color var(--transition-medium) ease;
        }

        .clear-filters-btn:hover {
            color: var(--color--link-text);
            background: var(--color--surface-2-bg);
            border-color: var(--color--border);
        }

        @media (max-width: 768px) {
            .filter-bar {
                flex-direction: column;
                align-items: stretch;
                width: 100%;
                box-sizing: border-box;
            }

            .filter-bar__controls {
                flex-direction: column;
                align-items: stretch;
            }

            .filter-divider {
                display: none;
            }

            .filter-group {
                flex-direction: column;
                align-items: stretch;
            }

            .filter-select {
                width: 100%;
            }

            .filter-bar__search {
                width: 100%;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, S25IconComponent, S25DatepickerModule, S25MultiselectModule],
})
export class SeEmailSentFilterComponent {
    readonly filter = model.required<EmailSentFilter>();
    readonly searchSubmit = output<void>();
    readonly clearFilters = output<void>();

    readonly showEventsFilter = input(true);
    readonly showOrganizationsFilter = input(true);
    /** When set, Clear resets to this baseline instead of {@link DEFAULT_EMAIL_SENT_FILTER}. */
    readonly clearFilterBaseline = input<EmailSentFilter | undefined>(undefined);

    readonly searchTermDraft = linkedSignal(() => this.filter().searchTerm);

    readonly organizationsModel = linkedSignal(() => this.filter().organizations ?? []);
    readonly eventsModel = linkedSignal(() => this.filter().events ?? []);

    readonly showCustomDates = computed(() => this.filter().datePreset === "custom");

    readonly hasActiveFilters = computed(() => {
        const filterState = this.filter();
        const organizationsActive = this.showOrganizationsFilter() && !!filterState.organizations?.length;
        const eventsActive = this.showEventsFilter() && !!filterState.events?.length;

        return (
            filterState.searchTerm !== "" || filterState.datePreset !== "last30" || organizationsActive || eventsActive
        );
    });

    onSearchTermDraftInput(value: string): void {
        this.searchTermDraft.set(value);
    }

    clearSearchTerm(): void {
        this.searchTermDraft.set("");
    }

    onSearchSubmit(): void {
        const searchTerm = this.searchTermDraft();
        if (this.filter().searchTerm !== searchTerm) {
            this.onFilterChange("searchTerm", searchTerm);
        }
        this.searchSubmit.emit();
    }

    onFilterChange<Key extends keyof EmailSentFilter>(key: Key, value: EmailSentFilter[Key]): void {
        this.filter.update((filterState) => ({ ...filterState, [key]: value }));
    }

    onOrganizationsDone(selected: DropDownItem[] | null | undefined): void {
        const organizations = selected?.length ? selected : undefined;
        this.onFilterChange("organizations", organizations);
        this.organizationsModel.set(selected ?? []);
    }

    onEventsDone(selected: DropDownItem[] | null | undefined): void {
        const events = selected?.length ? selected : undefined;
        this.onFilterChange("events", events);
        this.eventsModel.set(selected ?? []);
    }

    onClear(): void {
        const baseline = this.clearFilterBaseline() ?? DEFAULT_EMAIL_SENT_FILTER;
        this.filter.set({ ...baseline });
        this.searchTermDraft.set(baseline.searchTerm ?? "");
        this.organizationsModel.set(baseline.organizations ?? []);
        this.eventsModel.set(baseline.events ?? []);
        this.clearFilters.emit();
    }
}
